import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoadingSkeleton from './components/PageLoadingSkeleton';
import MarketingLayout from './components/MarketingLayout';
import Layout from './components/Layout';
import { Toaster } from 'sonner';
import axios from 'axios';
import './App.css';

// All public pages lazy-loaded (PR #53 — these were eager imports
// previously, bundled into main.js. Login/Signup/LandingPage are only
// ever rendered for unauthenticated visitors; lazy-loading removes
// ~1257 LOC + their dependencies from the authenticated user's
// initial bundle).
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const LandingPage = lazy(() => import('./pages/public/LandingPage'));

// Lazy-loaded public pages
const SignupSuccess = lazy(() => import('./pages/SignupSuccess'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const LoggedOut = lazy(() => import('./pages/LoggedOut'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const FeaturesPage = lazy(() => import('./pages/public/FeaturesPage'));
const HowItWorksPage = lazy(() => import('./pages/public/HowItWorksPage'));

// Lazy-loaded standalone public pages
const BookingEngine = lazy(() => import('./pages/BookingEngine'));
const GuestPortal = lazy(() => import('./pages/GuestPortal'));

// Lazy-loaded protected pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const FrontDesk = lazy(() => import('./pages/FrontDesk'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Reservations = lazy(() => import('./pages/Reservations'));
const Housekeeping = lazy(() => import('./pages/Housekeeping'));
const Guests = lazy(() => import('./pages/Guests'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const RatePlans = lazy(() => import('./pages/RatePlans'));
const ChannelManager = lazy(() => import('./pages/ChannelManager'));
const RevenueAnalytics = lazy(() => import('./pages/RevenueAnalytics'));
const AddOns = lazy(() => import('./pages/AddOns'));
const PromoCodes = lazy(() => import('./pages/PromoCodes'));
const Messaging = lazy(() => import('./pages/Messaging'));
const Staff = lazy(() => import('./pages/Staff'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const Forecasting = lazy(() => import('./pages/Forecasting'));
const ExportReports = lazy(() => import('./pages/ExportReports'));
const Accounting = lazy(() => import('./pages/Accounting'));
const Communications = lazy(() => import('./pages/Communications'));
const PaymentDiversity = lazy(() => import('./pages/PaymentDiversity'));
const SmartSystems = lazy(() => import('./pages/SmartSystems'));
const MobileStaff = lazy(() => import('./pages/MobileStaff'));
const MobileGuest = lazy(() => import('./pages/MobileGuest'));
const NightAudit = lazy(() => import('./pages/NightAudit'));
const GroupReservations = lazy(() => import('./pages/GroupReservations'));
const AdvancedAnalytics = lazy(() => import('./pages/AdvancedAnalytics'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const DNRManagement = lazy(() => import('./pages/DNRManagement'));
const PlatformAdmin = lazy(() => import('./pages/PlatformAdmin'));
const DatabaseAdmin = lazy(() => import('./pages/DatabaseAdmin'));
const AdminOrgDetail = lazy(() => import('./pages/AdminOrgDetail'));
const AdminPropertyDetail = lazy(() => import('./pages/AdminPropertyDetail'));

// Component to setup axios interceptors with access to navigate
function AxiosInterceptorSetup({ handleLogout }) {
  const navigate = useNavigate();

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Attempt token refresh before logging out
          const refreshToken = localStorage.getItem('refresh_token');
          const originalRequest = error.config;

          if (refreshToken && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const baseURL = process.env.REACT_APP_BACKEND_URL;
              const res = await axios.post(`${baseURL}/api/auth/refresh`, {
                refresh_token: refreshToken,
              });
              const newAccessToken = res.data.access_token;
              const newRefreshToken = res.data.refresh_token;
              localStorage.setItem('access_token', newAccessToken);
              if (newRefreshToken) {
                localStorage.setItem('refresh_token', newRefreshToken);
              }
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              return axios(originalRequest);
            } catch (refreshError) {
              // Refresh failed - log out
              console.log('[Auth] Refresh token expired, logging out');
              handleLogout();
              navigate('/login');
            }
          } else {
            handleLogout();
            navigate('/login');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, handleLogout]);

  return null;
}

// Protected route wrapper - reduces repetition
function ProtectedRoute({ user, onLogout }) {
  if (!user) return <Navigate to="/login" />;
  return <Layout user={user} onLogout={onLogout} />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, tokens) => {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('selected_property');
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <>
      <BrowserRouter>
        <AxiosInterceptorSetup handleLogout={handleLogout} />
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Routes>
              {/* Public Marketing Routes - With Header & Footer */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/dashboard" />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
                <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/dashboard" />} />
                <Route path="/signup-success" element={<SignupSuccess />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/logged-out" element={<LoggedOut />} />
              </Route>

              {/* Standalone Public Routes (no footer) */}
              <Route path="/book" element={<BookingEngine />} />
              <Route path="/guest-portal" element={<GuestPortal />} />

              {/* Protected PMS Application Routes */}
              <Route path="/dashboard" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={
                  <ErrorBoundary>
                    {user?.role === 'front_desk' ? <FrontDesk /> : <Dashboard />}
                  </ErrorBoundary>
                } />
              </Route>
              <Route path="/front-desk" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><FrontDesk /></ErrorBoundary>} />
              </Route>
              <Route path="/calendar" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Calendar /></ErrorBoundary>} />
              </Route>
              <Route path="/reservations" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Reservations /></ErrorBoundary>} />
              </Route>
              <Route path="/groups" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><GroupReservations /></ErrorBoundary>} />
              </Route>
              <Route path="/housekeeping" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Housekeeping /></ErrorBoundary>} />
              </Route>
              <Route path="/guests" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Guests /></ErrorBoundary>} />
              </Route>
              <Route path="/reports" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Reports /></ErrorBoundary>} />
              </Route>
              <Route path="/rate-plans" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><RatePlans /></ErrorBoundary>} />
              </Route>
              <Route path="/channel-manager" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><ChannelManager /></ErrorBoundary>} />
              </Route>
              <Route path="/revenue-analytics" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><RevenueAnalytics /></ErrorBoundary>} />
              </Route>
              <Route path="/addons" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><AddOns /></ErrorBoundary>} />
              </Route>
              <Route path="/promo-codes" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><PromoCodes /></ErrorBoundary>} />
              </Route>
              <Route path="/messaging" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Messaging /></ErrorBoundary>} />
              </Route>
              <Route path="/staff" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Staff /></ErrorBoundary>} />
              </Route>
              <Route path="/maintenance" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Maintenance /></ErrorBoundary>} />
              </Route>
              <Route path="/audit-logs" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><AuditLogs /></ErrorBoundary>} />
              </Route>
              <Route path="/forecasting" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Forecasting /></ErrorBoundary>} />
              </Route>
              <Route path="/export-reports" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><ExportReports /></ErrorBoundary>} />
              </Route>
              <Route path="/accounting" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Accounting /></ErrorBoundary>} />
              </Route>
              <Route path="/night-audit" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><NightAudit /></ErrorBoundary>} />
              </Route>
              <Route path="/communications" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Communications /></ErrorBoundary>} />
              </Route>
              <Route path="/payment-diversity" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><PaymentDiversity /></ErrorBoundary>} />
              </Route>
              <Route path="/smart-systems" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><SmartSystems /></ErrorBoundary>} />
              </Route>
              <Route path="/mobile-staff" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><MobileStaff /></ErrorBoundary>} />
              </Route>
              <Route path="/mobile-guest" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><MobileGuest /></ErrorBoundary>} />
              </Route>
              <Route path="/advanced-analytics" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><AdvancedAnalytics /></ErrorBoundary>} />
              </Route>
              <Route path="/user-management" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><UserManagement /></ErrorBoundary>} />
              </Route>
              <Route path="/dnr" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><DNRManagement /></ErrorBoundary>} />
              </Route>
              <Route path="/platform-admin" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><PlatformAdmin /></ErrorBoundary>} />
              </Route>
              <Route path="/database-admin" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><DatabaseAdmin /></ErrorBoundary>} />
              </Route>
              <Route path="/admin-org/:orgId" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><AdminOrgDetail /></ErrorBoundary>} />
              </Route>
              <Route path="/admin-property/:orgId/:propertyId" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><AdminPropertyDetail /></ErrorBoundary>} />
              </Route>
              <Route path="/settings" element={<ProtectedRoute user={user} onLogout={handleLogout} />}>
                <Route index element={<ErrorBoundary><Settings /></ErrorBoundary>} />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" expand={false} richColors />
    </>
  );
}

export default App;
